import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  DialogForm,
  CompactRichTextEditor,
  UserPicker,
  CollapsibleSection,
} from '../../../shared/components/common';
import {
  PendingAttachmentsField,
  useClipboardPaste,
  useUploadFile,
  type ClipboardRejectReason,
} from '../../../shared/files';
import { useAuth } from '../../../shared/auth/AuthContext';
import {
  useCreateWorkItem,
  useTransitionWorkItemStatus,
  useAddWorkItemCustomer,
} from '../hooks/useWorkItemQueries';
import { useModules } from '../hooks/useModuleQueries';
import { ModulePicker } from './ModulePicker';
import { InitiativePicker } from './InitiativePicker';
import { OrganizationPicker } from '../../master_data/contacts/components/OrganizationPicker';
import { settingsApi } from '../services/softwareFactoryApi';
import type { SfWorkItem } from '../types';
import autonomyLevelsConfig from '../../../shared-config/sf_autonomy_levels.config.json';
import dispatchProvidersConfig from '../../../shared-config/sf_dispatch_providers.config.json';
import {
  dispatchProviderIds,
  dispatchModelIds,
  dispatchProviderLabel,
  dispatchThinkingLevels,
  dispatchModelLabel,
} from '../utils/dispatchProviders';
import { WORK_ITEM_TYPE_VALUES as WORK_ITEM_TYPES } from '../../../generated/config-enums';
import { DEFAULT_WORK_ITEM_SIZE, WORK_ITEM_TITLE_MAX_LENGTH } from '../constants';

const AUTONOMY_LEVELS = Object.keys(autonomyLevelsConfig) as Array<keyof typeof autonomyLevelsConfig>;
const PROVIDER_IDS = dispatchProviderIds(dispatchProvidersConfig);

/** Matches the backend upload limit the file service enforces per file. */
const MAX_ATTACHMENT_SIZE_MB = 50;
const MAX_ATTACHMENTS = 10;

interface WorkItemQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  defaultModuleId?: string;
  /**
   * Pre-select a module by slug when the caller only knows the slug (e.g. a
   * sysadmin alert suggesting its owning module). Ignored when
   * `defaultModuleId` is given, and silently skipped when the tenant has no
   * module with that slug.
   */
  defaultModuleSlug?: string;
  defaultType?: string;
  /** Pre-select an autonomy level (key of sf_autonomy_levels.config.json). */
  defaultAutonomyLevel?: string;
  /**
   * Which of the two create actions the footer suggests. Defaults to `create`,
   * which leaves the item in `new` for triage — right when a human is filing
   * work they still have to think about.
   *
   * Callers whose item is ALREADY triaged by construction pass
   * `skipToPlanned`: a sysadmin alert is a machine-filed defect report with its
   * evidence attached, so the operator's intent is to get it dispatched, and
   * burying that behind a link-styled button made the routine action the harder
   * one. Both actions stay reachable either way — only which is primary moves.
   */
  primaryAction?: 'create' | 'skipToPlanned';
  defaultInitiativeId?: string;
  defaultTitle?: string;
  defaultDescription?: string;
  onCreated?: (workItem: SfWorkItem) => void;
}

interface QuickCreateFormData {
  title: string;
  type: string;
  module_id: string;
  priority: string;
  description: string;
  size: string;
  autonomy_level: string;
  preferred_provider: string;
  preferred_model: string;
  preferred_thinking_level: string;
  initiative_id: string;
  assignee_user_id: string;
  customer_organization_id: string;
}

const PRIORITIES = [
  { value: '0', label: '0' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
] as const;

export function WorkItemQuickCreateDialog({
  open,
  onClose,
  defaultModuleId,
  defaultModuleSlug,
  defaultType,
  defaultAutonomyLevel,
  primaryAction = 'create',
  defaultInitiativeId,
  defaultTitle,
  defaultDescription,
  onCreated,
}: WorkItemQuickCreateDialogProps) {
  const { t } = useTranslation(['softwareFactory', 'common', 'file_management']);
  const navigate = useNavigate();
  const { user } = useAuth();
  const createMutation = useCreateWorkItem();
  const transitionMutation = useTransitionWorkItemStatus();
  const addCustomerMutation = useAddWorkItemCustomer();
  const uploadMutation = useUploadFile();
  const skipToPlannedRef = useRef(false);
  // Slug→id resolution needs the module list, which may land after the dialog
  // opens; this guards the late fill so it never overwrites an operator's pick.
  const slugDefaultAppliedRef = useRef(false);
  // Same guard for the size default: the tenant's size list lands after the
  // dialog opens, so the fill happens in an effect and must never overwrite a
  // size the operator picked in the meantime.
  const sizeDefaultAppliedRef = useRef(false);

  const { data: modulesData } = useModules();
  const slugDefaultModuleId = useMemo(() => {
    if (defaultModuleId || !defaultModuleSlug) return '';
    return modulesData?.items.find((m) => m.slug === defaultModuleSlug)?.id ?? '';
  }, [defaultModuleId, defaultModuleSlug, modulesData?.items]);

  const handleRejectedFile = useCallback(
    (file: File, reason: ClipboardRejectReason) => {
      if (reason === 'size') {
        toast.error(t('file_management:maxSizeError', { size: MAX_ATTACHMENT_SIZE_MB }));
      }
    },
    [t]
  );

  const {
    pendingFiles,
    handlePaste,
    handleDrop,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleFileInputChange,
    removePendingFile,
    clearPendingFiles,
    isDragging,
  } = useClipboardPaste({
    maxFiles: MAX_ATTACHMENTS,
    maxSizeMB: MAX_ATTACHMENT_SIZE_MB,
    onReject: handleRejectedFile,
  });

  const { data: workItemSizes } = useQuery({
    queryKey: ['software-factory', 'work-item-sizes'],
    queryFn: settingsApi.getWorkItemSizes,
    staleTime: 5 * 60 * 1000,
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: {
      title: defaultTitle || '',
      type: defaultType || 'feature',
      module_id: defaultModuleId || '',
      priority: '2',
      description: defaultDescription || '',
      size: '',
      autonomy_level: defaultAutonomyLevel || '',
      preferred_provider: '',
      preferred_model: '',
      preferred_thinking_level: '',
      initiative_id: defaultInitiativeId || '',
      assignee_user_id: user?.id || '',
      customer_organization_id: '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        title: defaultTitle || '',
        type: defaultType || 'feature',
        module_id: defaultModuleId || '',
        priority: '2',
        description: defaultDescription || '',
        size: '',
        autonomy_level: defaultAutonomyLevel || '',
        // Reopening must clear the AI overrides too — omitting them here left the
        // previous pick in the form while the trigger showed a placeholder.
        preferred_provider: '',
        preferred_model: '',
        preferred_thinking_level: '',
        initiative_id: defaultInitiativeId || '',
        assignee_user_id: user?.id || '',
        customer_organization_id: '',
      });
      skipToPlannedRef.current = false;
      slugDefaultAppliedRef.current = false;
      sizeDefaultAppliedRef.current = false;
      clearPendingFiles();
    }
  }, [
    open,
    reset,
    clearPendingFiles,
    defaultModuleId,
    defaultModuleSlug,
    defaultType,
    defaultAutonomyLevel,
    defaultInitiativeId,
    defaultTitle,
    defaultDescription,
    user?.id,
  ]);

  useEffect(() => {
    if (!open || slugDefaultAppliedRef.current || !slugDefaultModuleId) return;
    // Never overwrite a module the operator already picked while the list loaded.
    if (getValues('module_id')) return;
    setValue('module_id', slugDefaultModuleId);
    slugDefaultAppliedRef.current = true;
  }, [open, slugDefaultModuleId, setValue, getValues]);

  // Start every new item at the middle of the effort scale, the way `priority`
  // starts at P2 — an estimate to adjust, not a claim, and better than an
  // unsized item that sorts and reports as if it were free. The vocabulary is
  // tenant-configurable, so this waits for the tenant's own list and applies
  // only where it carries the default: the size Select falls back to its "-"
  // placeholder for a value outside the list, so filling it blind would show a
  // dash and submit an "M" the tenant's picker cannot name.
  useEffect(() => {
    if (!open || sizeDefaultAppliedRef.current || !workItemSizes) return;
    sizeDefaultAppliedRef.current = true;
    if (getValues('size') || !workItemSizes.includes(DEFAULT_WORK_ITEM_SIZE)) return;
    setValue('size', DEFAULT_WORK_ITEM_SIZE);
  }, [open, workItemSizes, setValue, getValues]);

  const onSubmit = async (data: QuickCreateFormData) => {
    const skipToPlanned = skipToPlannedRef.current;
    let newWorkItem: SfWorkItem;
    try {
      // Attachments upload before the item exists, so they go up unattached and
      // are linked by id in the same create call. An abandoned dialog leaves
      // them orphaned; the file module's orphan reaper clears those after 24h.
      const attachmentIds = await Promise.all(
        pendingFiles.map((file) =>
          uploadMutation.mutateAsync({ file, is_user_file: false }).then((res) => res.data.id)
        )
      );

      newWorkItem = await createMutation.mutateAsync({
        title: data.title,
        type: data.type,
        module_id: data.module_id || undefined,
        priority: parseInt(data.priority, 10),
        description: data.description || undefined,
        size: data.size || undefined,
        autonomy_level: (data.autonomy_level || null) as SfWorkItem['autonomy_level'],
        preferred_provider: (data.preferred_provider || null) as SfWorkItem['preferred_provider'],
        preferred_model: data.preferred_model || null,
        preferred_thinking_level: (data.preferred_thinking_level || null) as SfWorkItem['preferred_thinking_level'],
        initiative_id: data.initiative_id || undefined,
        assignee_user_id: data.assignee_user_id || undefined,
        attachment_ids: attachmentIds.length > 0 ? attachmentIds : undefined,
      });
    } catch {
      // The global mutation-error handler shows the specific translated toast.
      // Nothing was created, so the operator keeps the form and can retry.
      return;
    }

    // Past this point the work item EXISTS. The follow-ups below are separate
    // calls that can fail on their own, and a failure must not strand the
    // operator on a dialog for an item that is already in the backlog — the
    // global handler names what failed, and the detail page is where they fix it.
    try {
      // The customer link is a sub-resource, so it can only be written once the
      // work item exists.
      if (data.customer_organization_id) {
        await addCustomerMutation.mutateAsync({
          id: newWorkItem.id,
          organization_id: data.customer_organization_id,
        });
      }

      if (skipToPlanned) {
        // Transition new → triaged → planned
        await transitionMutation.mutateAsync({
          id: newWorkItem.id,
          data: { status: 'triaged' },
        });
        await transitionMutation.mutateAsync({
          id: newWorkItem.id,
          data: { status: 'planned' },
        });
      }
    } catch {
      // Reported by the global mutation-error handler; still open the item.
    }

    onCreated?.(newWorkItem);
    onClose();
    navigate(`/software-factory/work-items/${newWorkItem.id}`);
  };

  const handleSkipToPlanned = () => {
    skipToPlannedRef.current = true;
    handleSubmit(onSubmit)();
  };

  // Both actions set the ref explicitly: it is only reset when the dialog
  // reopens, so a failed "skip to planned" followed by a plain "create" would
  // otherwise still transition the new item to planned.
  const handleCreateOnly = () => {
    skipToPlannedRef.current = false;
    handleSubmit(onSubmit)();
  };

  const createAction = {
    label: t('common:app.create'),
    onClick: handleCreateOnly,
    pending: !skipToPlannedRef.current,
  };
  const skipToPlannedAction = {
    label: t('softwareFactory:workItems.createSkipToPlanned'),
    onClick: handleSkipToPlanned,
    pending: skipToPlannedRef.current,
  };
  const primary = primaryAction === 'skipToPlanned' ? skipToPlannedAction : createAction;
  const secondary = primaryAction === 'skipToPlanned' ? createAction : skipToPlannedAction;

  // The AI overrides are collapsed by default, so the trigger has to say when a
  // pick is hiding inside it — otherwise a stale override ships unseen.
  const watchedProvider = watch('preferred_provider');
  const watchedModel = watch('preferred_model');
  const watchedLevel = watch('preferred_thinking_level');
  const aiPreferencesSummary = watchedProvider
    ? [
        dispatchProviderLabel(dispatchProvidersConfig, watchedProvider),
        watchedModel && dispatchModelLabel(dispatchProvidersConfig, watchedProvider, watchedModel),
        watchedLevel && t(`softwareFactory:workItems.aiSetups.levels.${watchedLevel}`),
      ]
        .filter(Boolean)
        .join(' · ')
    : t('softwareFactory:workItems.aiPreferencesDefault');

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('softwareFactory:workItems.create')}
      onSubmit={handleSubmit(onSubmit)}
      isLoading={isSubmitting}
      maxWidth="lg"
      hideActions
    >
      {/* Paste/drop are wired on the whole body so Cmd+V works from any field —
          including the description editor — not only over the attachments row.
          Both are ignored once the submit is in flight: that upload batch has
          already been taken, so a late file would show as attached and never be
          uploaded. The drop is still swallowed so the browser does not navigate
          away to the dropped file. */}
      <div
        className="space-y-3"
        onPaste={isSubmitting ? undefined : handlePaste}
        onDrop={isSubmitting ? (e) => e.preventDefault() : handleDrop}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
      >
        <div className="space-y-2">
          <Label htmlFor="title" required>{t('softwareFactory:workItems.titleField')}</Label>
          <Controller
            name="title"
            control={control}
            rules={{
              required: t('common:validation.required'),
              maxLength: {
                value: WORK_ITEM_TITLE_MAX_LENGTH,
                message: t('common:validation.maxLength', { max: WORK_ITEM_TITLE_MAX_LENGTH }),
              },
            }}
            render={({ field }) => (
              <Input
                {...field}
                id="title"
                autoFocus
                maxLength={WORK_ITEM_TITLE_MAX_LENGTH}
                className={cn(errors.title && 'border-destructive')}
              />
            )}
          />
          {errors.title && (
            <p className="text-sm text-destructive">{errors.title.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label required>{t('softwareFactory:workItems.type')}</Label>
            <Controller
              name="type"
              control={control}
              rules={{ required: t('common:validation.required') }}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger aria-label={t('softwareFactory:workItems.type')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WORK_ITEM_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {t(`softwareFactory:types.${type}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('softwareFactory:workItems.priority')}</Label>
            <Controller
              name="priority"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger aria-label={t('softwareFactory:workItems.priority')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {t(`softwareFactory:priorities.${p.label}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Controller
            name="module_id"
            control={control}
            render={({ field }) => (
              <ModulePicker
                value={field.value || null}
                onChange={(id) => field.onChange(id || '')}
                label={t('softwareFactory:workItems.module')}
              />
            )}
          />

          <Controller
            name="initiative_id"
            control={control}
            render={({ field }) => (
              <InitiativePicker
                value={field.value || null}
                onChange={(id) => field.onChange(id || '')}
                label={t('softwareFactory:initiatives.entity')}
              />
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>{t('softwareFactory:workItems.autonomyLevel')}</Label>
            <Controller
              name="autonomy_level"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger aria-label={t('softwareFactory:workItems.autonomyLevel')}>
                    <SelectValue placeholder={t('softwareFactory:workItems.autonomyLevelPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {AUTONOMY_LEVELS.map((level) => (
                      <SelectItem key={level} value={level}>
                        <div className="flex flex-col">
                          <span>{t(`softwareFactory:autonomyLevels.${level}.label`)}</span>
                          <span className="text-xs text-muted-foreground">
                            {t(`softwareFactory:autonomyLevels.${level}.description`)}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {workItemSizes && workItemSizes.length > 0 && (
            <div className="space-y-2">
              <Label>{t('softwareFactory:workItems.size')}</Label>
              <Controller
                name="size"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger aria-label={t('softwareFactory:workItems.size')}>
                      <SelectValue placeholder="-" />
                    </SelectTrigger>
                    <SelectContent>
                      {workItemSizes.map((size) => (
                        <SelectItem key={size} value={size}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Controller
            name="assignee_user_id"
            control={control}
            render={({ field }) => (
              <UserPicker
                value={field.value || null}
                onChange={(id) => field.onChange(id || '')}
                label={t('softwareFactory:workItems.assignee')}
                filterUserTypes={['internal', 'ai_agent']}
              />
            )}
          />

          <Controller
            name="customer_organization_id"
            control={control}
            render={({ field }) => (
              <OrganizationPicker
                value={field.value}
                onChange={field.onChange}
                label={t('softwareFactory:workItems.customers.organizationLabel')}
                allowClear
              />
            )}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('softwareFactory:workItems.description')}</Label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <CompactRichTextEditor
                value={field.value}
                onChange={field.onChange}
                minHeight={80}
              />
            )}
          />
        </div>

        <PendingAttachmentsField
          files={pendingFiles}
          onFileInputChange={handleFileInputChange}
          onRemove={removePendingFile}
          isDragging={isDragging}
          maxFiles={MAX_ATTACHMENTS}
          disabled={isSubmitting}
        />

        <CollapsibleSection
          label={t('softwareFactory:workItems.aiPreferences')}
          summary={aiPreferencesSummary}
        >
          {/* The grid lives inside the children, not on `contentClassName` —
              that wrapper carries `space-y-3`, which would stack a margin onto
              every column after the first and knock the rows out of alignment. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>{t('softwareFactory:workItems.preferredProvider')}</Label>
            <Controller
              name="preferred_provider"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    // Model AND thinking-level options both hang off the provider —
                    // drop any pick the new provider does not offer.
                    setValue('preferred_model', '');
                    setValue('preferred_thinking_level', '');
                  }}
                >
                  <SelectTrigger aria-label={t('softwareFactory:workItems.preferredProvider')}>
                    <SelectValue placeholder={t('softwareFactory:workItems.preferredProviderPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVIDER_IDS.map((provider) => (
                      <SelectItem key={provider} value={provider}>
                        {dispatchProviderLabel(dispatchProvidersConfig, provider)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('softwareFactory:workItems.preferredModel')}</Label>
            <Controller
              name="preferred_model"
              control={control}
              render={({ field }) => {
                const provider = watch('preferred_provider');
                const modelIds = dispatchModelIds(dispatchProvidersConfig, provider);
                return (
                  <Select
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value);
                      // A model may narrow the provider's ladder (Haiku offers none),
                      // so a level the new model does not accept is dropped.
                      const offered = dispatchThinkingLevels(dispatchProvidersConfig, provider, value);
                      if (!offered.includes(watch('preferred_thinking_level'))) {
                        setValue('preferred_thinking_level', '');
                      }
                    }}
                    disabled={!provider}
                  >
                    <SelectTrigger aria-label={t('softwareFactory:workItems.preferredModel')}>
                      <SelectValue
                        placeholder={
                          provider
                            ? t('softwareFactory:workItems.preferredModelPlaceholder')
                            : t('softwareFactory:workItems.preferredModelNoProvider')
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {modelIds.map((model) => (
                        <SelectItem key={model} value={model}>
                          {dispatchModelLabel(dispatchProvidersConfig, provider, model)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              }}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('softwareFactory:workItems.preferredThinkingLevel')}</Label>
            <Controller
              name="preferred_thinking_level"
              control={control}
              render={({ field }) => {
                const provider = watch('preferred_provider');
                const levels = dispatchThinkingLevels(
                  dispatchProvidersConfig,
                  provider,
                  watch('preferred_model'),
                );
                return (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={!provider || levels.length === 0}
                  >
                    <SelectTrigger aria-label={t('softwareFactory:workItems.preferredThinkingLevel')}>
                      <SelectValue
                        placeholder={
                          !provider
                            ? t('softwareFactory:workItems.preferredThinkingLevelNoProvider')
                            : levels.length === 0
                              ? t('softwareFactory:workItems.preferredThinkingLevelNotSupported')
                              : t('softwareFactory:workItems.preferredThinkingLevelPlaceholder')
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {levels.map((level) => (
                        <SelectItem key={level} value={level}>
                          {t(`softwareFactory:workItems.aiSetups.levels.${level}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              }}
            />
            </div>
          </div>
        </CollapsibleSection>
      </div>

      <div className="flex items-center justify-between pt-4 mt-4 border-t">
        {/* The two create actions swap slots on `primaryAction`; each keeps its
            own label, handler and spinner, so only the emphasis moves. */}
        <Button
          type="button"
          variant="link"
          size="sm"
          onClick={secondary.onClick}
          disabled={isSubmitting}
          className="px-0 text-muted-foreground"
        >
          {isSubmitting && secondary.pending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
          {secondary.label}
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {t('common:app.cancel')}
          </Button>
          <Button onClick={primary.onClick} disabled={isSubmitting}>
            {isSubmitting && primary.pending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
            {primary.label}
          </Button>
        </div>
      </div>
    </DialogForm>
  );
}
